import axios from "axios";
import { base_url } from "./baseUrl";

// Create an axios instance with default configuration
const apiClient = axios.create({
  baseURL: base_url,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Token refresh in progress flag to prevent multiple concurrent refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Get token from localStorage if available
    const getTokenFromLocalStorage = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;

    if (getTokenFromLocalStorage && getTokenFromLocalStorage.token) {
      config.headers.Authorization = `Bearer ${getTokenFromLocalStorage.token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors (token expired)
    if ((error.response?.status === 401 || error.response?.status === 500) && !originalRequest._retry) {
      // Check if it's specifically a token expiration error that can be refreshed
      if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // This is a token expiration error that we should try to refresh
        if (isRefreshing) {
          // If refresh is already in progress, queue the request
          return new Promise(function(resolve, reject) {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return apiClient(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem("refreshToken");
        
        if (refreshToken) {
          try {
            const response = await axios.get(`${base_url}user/refresh`, {
              withCredentials: true // Include cookies for refresh token
            });
            
            const { accessToken } = response.data;
            
            // Update token in localStorage
            const user = JSON.parse(localStorage.getItem("user"));
            user.token = accessToken;
            localStorage.setItem("user", JSON.stringify(user));
            
            // Update the original request with new token
            originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
            
            processQueue(null, accessToken);
            return apiClient(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            // Refresh failed, logout user
            localStorage.removeItem("user");
            localStorage.removeItem("refreshToken");
            window.location.href = "/admin/login";
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        } else {
          // No refresh token, logout user
          localStorage.removeItem("user");
          window.location.href = "/admin/login";
        }
      } else {
        // For other 401 errors (like session invalidation), don't attempt refresh
        localStorage.removeItem("user");
        localStorage.removeItem("refreshToken");
        window.location.href = "/admin/login";
        return Promise.reject(error);
      }
    }

    // Handle common error responses
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // Handle unauthorized access
          localStorage.removeItem("user");
          localStorage.removeItem("refreshToken");
          window.location.href = "/admin/login";
          break;
        case 403:
          // Handle forbidden access
          console.error("Access forbidden:", error.response.data);
          break;
        case 500:
          console.error("Server error:", error.response.data);
          break;
        default:
          console.error("API error:", error.response.data);
      }
    } else if (error.request) {
      console.error("Network error:", error.request);
    } else {
      console.error("Error:", error.message);
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;

// Create a function to get dynamic config with current token
export const getConfig = () => {
  const getTokenFromLocalStorage = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;

  return {
    headers: {
      Authorization: `Bearer ${
        getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
      }`,
      Accept: "application/json",
    },
  };
};

// Keep the old config for backward compatibility, but note it's static
const getTokenFromLocalStorage = localStorage.getItem("user")
  ? JSON.parse(localStorage.getItem("user"))
  : null;

export const config = {
  headers: {
    Authorization: `Bearer ${
      getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
    }`,
    Accept: "application/json",
  },
};